import { AlertDto } from '@/models/suba/alerts/alert-dto.model';
import { useAlertContext } from '@/routes/suba/common/contexts/AlertContext';
import { AlertSubType } from '@vestwell-sub-accounting/models/common/AlertSubType';
import { CustodianId } from '@vestwell-sub-accounting/models/common/CustodianId';
import { ParentAccountType } from '@vestwell-sub-accounting/models/common/ParentAccountType';

import { FC, useMemo } from 'react';

import { NewConversionDepositExpandedAlertDetails } from './conversion/new-conversion-deposit/NewConversionDepositExpandedAlertDetails.component';
import { DisbursementExpandedAlertDetails } from './disbursement/disbursement-request/DisbursementExpandedAlertDetails.component';
import { AchReversalExpandedAlertDetails } from './money-in/ach-reversal/AchReversalExpandedAlertDetails.component';
import { CashTransferRejectedExpandedAlertDetails } from './money-in/cash-transfer-rejected/CashTransferRejectedExpandedAlertDetails.component';
import { UnclassifiedDepositExpandedAlertDetails } from './money-in/unclassified-deposit/UnclassifiedDepositExpandedAlertDetails.component';
import { NewRolloverDepositExpandedAlertDetails } from './rollovers/new-rollover-deposit/NewRolloverDepositExpandedAlertDetails.component';
import { CustodianDestinationRejectExpandedAlertDetails } from './trading/common/CustodianDestinationRejectExpandedAlertDetails.component';

const getExpandedAlertDetailsComponent = (alert?: AlertDto) => {
  if (
    !alert ||
    (alert.metadata?.custodianIds &&
      alert.metadata.custodianIds.length > 0 &&
      !alert.metadata.custodianIds.includes(CustodianId.matrix))
  ) {
    return null;
  }

  switch (alert.alertSubType) {
    case AlertSubType.NewConversionDeposit:
      return NewConversionDepositExpandedAlertDetails;
    case AlertSubType.NewRolloverDeposit:
      return NewRolloverDepositExpandedAlertDetails;
    case AlertSubType.UnclassifiedDeposit:
      return UnclassifiedDepositExpandedAlertDetails;
    case AlertSubType.DestinationReject:
    case AlertSubType.CustodianReject:
      return CustodianDestinationRejectExpandedAlertDetails;
    case AlertSubType.CashTransferRejected:
      return 'request' in alert.details && alert.details.request
        ? CashTransferRejectedExpandedAlertDetails
        : null;
    case AlertSubType.ACHReversal:
      return alert.parentAccount?.accountType === ParentAccountType.PlanLevel
        ? AchReversalExpandedAlertDetails
        : null;
    case AlertSubType.DisbursementRequest:
      return DisbursementExpandedAlertDetails;
    default:
      return null;
  }
};

export const hasExpandedAlertDetails = (alert?: AlertDto) => {
  return !!getExpandedAlertDetailsComponent(alert);
};

/**
 * @returns Component corresponding to alert in context's sub-type
 */
export const ExpandedAlertDetails: FC = () => {
  // context

  const alert = useAlertContext<AlertDto>();

  // memos

  const ExpandedAlertDetailsComponent = useMemo(() => {
    return getExpandedAlertDetailsComponent(alert);
  }, [alert]);

  return ExpandedAlertDetailsComponent ? (
    <ExpandedAlertDetailsComponent />
  ) : null;
};
